// src/components/Header.js
import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/img/logo.png';
import defaultProfile from '../assets/img/default-profile.jpg';
import SearchModal from './modals/SearchModal'; // Import SearchModal
import config from '../config/config';

const Header = () => {
  const { currentUser, logout } = useAuth();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const navbarToggleRef = useRef(null);

  // Helper function to safely get user type
  const getUserType = () => {
    if (!currentUser) return null;
    return currentUser.user_type || (currentUser.user && currentUser.user.user_type);
  };

  // Helper function to safely get username
  const getUserName = () => {
    if (!currentUser) return 'Guest';
    return currentUser.name || (currentUser.user && currentUser.user.name) || 'User';
  };

  // Function to close the mobile menu
  const handleNavLinkClick = () => {
    if (window.innerWidth < 768) {
      // For Bootstrap 5, directly manipulate the DOM
      const navbarCollapse = document.getElementById('navbarSupportedContent');
      if (navbarCollapse && navbarCollapse.classList.contains('show')) {
        // Find the navbar toggler and click it to close the menu
        const toggler = document.querySelector('.navbar-toggler');
        if (toggler) {
          toggler.click();
        }
      }
      setIsNavCollapsed(true);
    }
  };

  useEffect(() => {
    // Sticky navbar logic
    window.onscroll = function () {
      myFunction();
    };

    const navbar_sticky = document.getElementById('navbar_sticky');
    if (!navbar_sticky) return;
    const sticky = navbar_sticky.offsetTop;
    const navbar_height = navbar_sticky.offsetHeight;

    function myFunction() {
      if (window.pageYOffset >= sticky + navbar_height) {
        navbar_sticky.classList.add('sticky');
        document.body.style.paddingTop = navbar_height + 'px';
      } else {
        navbar_sticky.classList.remove('sticky');
        document.body.style.paddingTop = '0';
      }
    }

    // Add background color to mobile menu
    const addMobileMenuBackground = () => {
      const navbarCollapse = document.getElementById('navbarSupportedContent');
      if (navbarCollapse) {
        if (window.innerWidth < 768) {
          navbarCollapse.style.backgroundColor = '#212529';
          navbarCollapse.style.padding = '10px';
          navbarCollapse.style.borderRadius = '5px';
        } else {
          navbarCollapse.style.backgroundColor = '';
          navbarCollapse.style.padding = '';
          navbarCollapse.style.borderRadius = '';
        }
      }
    };

    // Call once on load
    addMobileMenuBackground();

    // Add event listener for resize
    window.addEventListener('resize', addMobileMenuBackground);

    // Cleanup function
    return () => {
      window.onscroll = null;
      window.removeEventListener('resize', addMobileMenuBackground);
    };
  }, []);

  const handleSearchClose = () => {
    setShowSearchModal(false);
  };

  const getProfileImage = () => {
    if (!currentUser) return defaultProfile;
    if (currentUser.profile_image) {
      return `${config.IMAGE_URL}/profiles/${currentUser.profile_image}`;
    }
    return defaultProfile;
  };

  return (
    <>
      <section id="header" style={{ position: 'fixed', top: 0, width: '100%', zIndex: 3 }}>
        <nav className="navbar navbar-expand-md navbar-light" id="navbar_sticky">
          <div className="container">
            <Link className="navbar-brand fw-bold" to="/">
              <img src={logo} width="50" height="70" alt="SPONO Logo" /> S P O N O
            </Link>
            <button
              ref={navbarToggleRef}
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon">
                {/* Custom hamburger icon with white color */}
                <i className="fa fa-bars text-white"></i>
              </span>
            </button>
            <div
              className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse`}
              id="navbarSupportedContent"
            >
              <ul className="navbar-nav mb-0 ms-auto" id="nav-items">
                <li className="nav-item">
                  <NavLink
                    to="/"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    onClick={handleNavLinkClick}
                  >
                    Home
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    to="/about"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    onClick={handleNavLinkClick}
                  >
                    About Us
                  </NavLink>
                </li>
                {currentUser ? (
                  <>
                    {/* Show "Moments" only for event_attendee users */}
                    {getUserType() === 'event_attendee' && (
                      <li className="nav-item">
                        <NavLink
                          to="/moments"
                          className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                          onClick={handleNavLinkClick}
                        >
                          Moments
                        </NavLink>
                      </li>
                    )}
                    {/* Conditionally render "Create Events" and "My Events" for event promoters */}
                    {getUserType() === 'event_promoter' && (
                      <>
                        <li className="nav-item">
                          <NavLink
                            to="/create-event"
                            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            onClick={handleNavLinkClick}
                          >
                            Create Events
                          </NavLink>
                        </li>
                        <li className="nav-item">
                          <NavLink
                            to="/my-events"
                            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            onClick={handleNavLinkClick}
                          >
                            My Events
                          </NavLink>
                        </li>
                      </>
                    )}
                    <li className="nav-item dropdown" style={{ marginTop: '-5px' }}>
                      <button
                        className="nav-link dropdown-toggle d-flex align-items-center"
                        id="userDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="true"
                        style={{ background: 'none', border: 'none', color: 'white' }}
                      >
                        <img
                          src={getProfileImage()}
                          alt="Profile"
                          className="profile-icon"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            marginRight: '10px',
                          }}
                        />
                        <span className="username">{getUserName()}</span>
                      </button>
                      {/* Profile dropdown - Added bg-transparent and removed card classes */}
                      <div className="dropdown-menu dropdown-menu-end  border-0" aria-labelledby="profileDropdown">
                        <div className="px-3 py-2 bg-transparent">
                          <h6 className="text-white mb-0">{getUserName()}</h6>
                          <p className="text-white-50 small mb-0">{currentUser.email}</p>
                        </div>
                        <div className="dropdown-divider"></div>
                        <Link className="dropdown-item text-white bg-transparent" to="/profile">
                          <i className="fa fa-user me-2"></i> Profile
                        </Link>
                        <Link className="dropdown-item text-white bg-transparent" to="/my-events">
                          <i className="fa fa-calendar me-2"></i> My Events
                        </Link>
                        <Link className="dropdown-item text-white bg-transparent" to="/settings">
                          <i className="fa fa-cog me-2"></i> Settings
                        </Link>
                        <div className="dropdown-divider"></div>
                        <button className="dropdown-item text-white bg-transparent" onClick={() => {logout(); handleNavLinkClick();}}>
                          <i className="fa fa-sign-out me-2"></i> Logout
                        </button>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    {/* Removed Events tab for non-logged in users */}
                    <li className="nav-item">
                      <NavLink to="/login" className="nav-link" onClick={handleNavLinkClick}>
                        <button className="button">Log In/Sign Up</button>
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>

              {/* Search Icon Button */}
              <button
                className="btn"
                id="searchIconBtn"
                aria-label="Search"
                style={{ color: 'white' }}
                onClick={() => {setShowSearchModal(true); handleNavLinkClick();}}
              >
                <i className="fa fa-search"></i>
              </button>
            </div>
          </div>
        </nav>
      </section>

      {/* Search Modal */}
      <SearchModal show={showSearchModal} onClose={handleSearchClose} />

      {/* Add custom CSS to ensure the hamburger icon is white and properly sized */}
      <style jsx="true">{`
        .navbar-toggler {
          border-color: rgba(255, 255, 255, 0.5);
          padding: 0.25rem 0.5rem; /* Smaller padding */
          font-size: 0.8rem; /* Smaller font size */
        }
        
        .navbar-toggler-icon {
          background-image: none !important;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1.6em; /* Smaller icon width */
          height: 1.6em; /* Smaller icon height */
        }
        
        .navbar-toggler:focus {
          box-shadow: 0 0 0 0.15rem rgba(255, 255, 255, 0.25);
        }
        
        .navbar-toggler .fa-bars {
          font-size: 1rem; /* Smaller bars icon */
        }
      `}</style>
    </>
  );
};

export default Header;
